import React from 'react';

interface AITelemetryDashboardProps {
  className?: string;
  style?: React.CSSProperties;
}

export default function AITelemetryDashboard({ className = '', style = {} }: AITelemetryDashboardProps) {
  return (
    <div
      className={className}
      style={{
        width: '100%',
        maxWidth: '900px',
        height: '500px',
        background: 'rgba(15, 23, 42, 0.8)',
        border: '1px solid rgba(71, 85, 105, 0.3)',
        borderRadius: '16px',
        padding: '24px',
        backdropFilter: 'blur(8px)',
        position: 'relative',
        overflow: 'hidden',
        ...style
      }}
    >
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
        paddingBottom: '16px',
        borderBottom: '1px solid rgba(71, 85, 105, 0.3)'
      }}>
        <h3 style={{
          color: 'white',
          fontSize: '18px',
          fontWeight: 'bold',
          margin: 0
        }}>AI Performance Dashboard</h3>
        <div style={{
          display: 'flex',
          gap: '8px'
        }}>
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: '#10b981'
          }} />
          <span style={{ color: '#94a3b8', fontSize: '12px' }}>Live</span>
        </div>
      </div>

      {/* Main Content Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gridTemplateRows: '1fr 1fr',
        gap: '16px',
        height: 'calc(100% - 80px)'
      }}>
        {/* Performance Metrics */}
        <div style={{
          background: 'rgba(30, 41, 59, 0.6)',
          borderRadius: '12px',
          padding: '16px',
          border: '1px solid rgba(71, 85, 105, 0.2)'
        }}>
          <h4 style={{
            color: 'white',
            fontSize: '14px',
            fontWeight: '500',
            margin: '0 0 12px 0'
          }}>Response Time</h4>
          <div style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: '8px',
            marginBottom: '12px'
          }}>
            <span style={{
              color: '#3b82f6',
              fontSize: '24px',
              fontWeight: 'bold'
            }}>142ms</span>
            <span style={{
              color: '#10b981',
              fontSize: '12px'
            }}>↓ 15%</span>
          </div>
          <div style={{
            height: '60px',
            background: 'linear-gradient(90deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.3) 50%, rgba(59, 130, 246, 0.1) 100%)',
            borderRadius: '4px',
            position: 'relative'
          }}>
            <div style={{
              position: 'absolute',
              bottom: '0',
              left: '10%',
              width: '4px',
              height: '40%',
              background: '#3b82f6',
              borderRadius: '2px'
            }} />
            <div style={{
              position: 'absolute',
              bottom: '0',
              left: '25%',
              width: '4px',
              height: '60%',
              background: '#3b82f6',
              borderRadius: '2px'
            }} />
            <div style={{
              position: 'absolute',
              bottom: '0',
              left: '40%',
              width: '4px',
              height: '80%',
              background: '#3b82f6',
              borderRadius: '2px'
            }} />
            <div style={{
              position: 'absolute',
              bottom: '0',
              left: '55%',
              width: '4px',
              height: '50%',
              background: '#3b82f6',
              borderRadius: '2px'
            }} />
            <div style={{
              position: 'absolute',
              bottom: '0',
              left: '70%',
              width: '4px',
              height: '35%',
              background: '#3b82f6',
              borderRadius: '2px'
            }} />
            <div style={{
              position: 'absolute',
              bottom: '0',
              left: '85%',
              width: '4px',
              height: '45%',
              background: '#3b82f6',
              borderRadius: '2px'
            }} />
          </div>
        </div>

        {/* Model Accuracy */}
        <div style={{
          background: 'rgba(30, 41, 59, 0.6)',
          borderRadius: '12px',
          padding: '16px',
          border: '1px solid rgba(71, 85, 105, 0.2)'
        }}>
          <h4 style={{
            color: 'white',
            fontSize: '14px',
            fontWeight: '500',
            margin: '0 0 12px 0'
          }}>Model Accuracy</h4>
          <div style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: '8px',
            marginBottom: '12px'
          }}>
            <span style={{
              color: '#10b981',
              fontSize: '24px',
              fontWeight: 'bold'
            }}>94.7%</span>
            <span style={{
              color: '#10b981',
              fontSize: '12px'
            }}>↑ 3.2%</span>
          </div>
          <div style={{
            width: '100%',
            height: '8px',
            background: 'rgba(71, 85, 105, 0.3)',
            borderRadius: '4px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: '94.7%',
              height: '100%',
              background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)',
              borderRadius: '4px'
            }} />
          </div>
        </div>

        {/* Cost Optimization */}
        <div style={{
          background: 'rgba(30, 41, 59, 0.6)',
          borderRadius: '12px',
          padding: '16px',
          border: '1px solid rgba(71, 85, 105, 0.2)'
        }}>
          <h4 style={{
            color: 'white',
            fontSize: '14px',
            fontWeight: '500',
            margin: '0 0 12px 0'
          }}>Cost Savings</h4>
          <div style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: '8px',
            marginBottom: '12px'
          }}>
            <span style={{
              color: '#8b5cf6',
              fontSize: '24px',
              fontWeight: 'bold'
            }}>$12.4K</span>
            <span style={{
              color: '#10b981',
              fontSize: '12px'
            }}>↑ 28%</span>
          </div>
          <div style={{
            display: 'flex',
            gap: '4px',
            height: '40px',
            alignItems: 'end'
          }}>
            {[20, 35, 45, 30, 60, 55, 40, 50].map((height, index) => (
              <div
                key={index}
                style={{
                  flex: 1,
                  height: `${height}%`,
                  background: `linear-gradient(180deg, #8b5cf6 0%, #7c3aed ${height}%)`,
                  borderRadius: '2px'
                }}
              />
            ))}
          </div>
        </div>

        {/* System Health */}
        <div style={{
          background: 'rgba(30, 41, 59, 0.6)',
          borderRadius: '12px',
          padding: '16px',
          border: '1px solid rgba(71, 85, 105, 0.2)'
        }}>
          <h4 style={{
            color: 'white',
            fontSize: '14px',
            fontWeight: '500',
            margin: '0 0 12px 0'
          }}>System Health</h4>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: '#94a3b8', fontSize: '12px' }}>CPU</span>
              <span style={{ color: '#10b981', fontSize: '12px' }}>67%</span>
            </div>
            <div style={{
              width: '100%',
              height: '4px',
              background: 'rgba(71, 85, 105, 0.3)',
              borderRadius: '2px'
            }}>
              <div style={{
                width: '67%',
                height: '100%',
                background: '#10b981',
                borderRadius: '2px'
              }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: '#94a3b8', fontSize: '12px' }}>Memory</span>
              <span style={{ color: '#f59e0b', fontSize: '12px' }}>82%</span>
            </div>
            <div style={{
              width: '100%',
              height: '4px',
              background: 'rgba(71, 85, 105, 0.3)',
              borderRadius: '2px'
            }}>
              <div style={{
                width: '82%',
                height: '100%',
                background: '#f59e0b',
                borderRadius: '2px'
              }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: '#94a3b8', fontSize: '12px' }}>Disk</span>
              <span style={{ color: '#10b981', fontSize: '12px' }}>45%</span>
            </div>
            <div style={{
              width: '100%',
              height: '4px',
              background: 'rgba(71, 85, 105, 0.3)',
              borderRadius: '2px'
            }}>
              <div style={{
                width: '45%',
                height: '100%',
                background: '#10b981',
                borderRadius: '2px'
              }} />
            </div>
          </div>
        </div>
      </div>

      {/* Background gradient overlay */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 30% 20%, rgba(59, 130, 246, 0.1) 0%, transparent 50%), radial-gradient(circle at 70% 80%, rgba(139, 92, 246, 0.1) 0%, transparent 50%)',
        pointerEvents: 'none',
        borderRadius: '16px'
      }} />
    </div>
  );
}